/* eslint-disable react/prop-types, prettier/prettier */
import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { keyframes } from '@emotion/react'
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    CircularProgress,
    Chip,
    Tooltip,
    IconButton,
    Collapse,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PolylineIcon from '@mui/icons-material/Polyline'
import ArticleIcon from '@mui/icons-material/Article'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import EmailIcon from '@mui/icons-material/Email'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import SmsIcon from '@mui/icons-material/Sms'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import AIService from '../../services/aiService'
import { useToolRun } from '../../helpers/toolRun'
import ErrorBanner from '../../components/shared/ErrorBanner'
import {
    ACCENT,
    BRAND,
    CATEGORY,
    CHANNEL_COLORS,
    FONT,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    cardSx,
    pageSx,
    tint,
} from '../../theme/tokens'

const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`
const shimmer = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`

const CHANNEL_ICONS = { whatsapp: WhatsAppIcon, email: EmailIcon, sms: SmsIcon }
// The app's channel colours (text-safe on their tint in both themes).
const CHANNEL_COLOR = {
    whatsapp: CHANNEL_COLORS.WHATSAPP,
    email: CHANNEL_COLORS.EMAIL,
    sms: CHANNEL_COLORS.SMS,
}

// A send's status as text on its own faint tint; every colour is a
// text-safe STATUS or CATEGORY token (contrast.ai-a.test.js).
export const STATUS_CONFIG = {
    sent: { color: STATUS.success.fg, Icon: DoneAllIcon, label: 'Sent' },
    delivered: { color: STATUS.info.fg, Icon: DoneAllIcon, label: 'Delivered' },
    failed: { color: STATUS.error.fg, Icon: CancelIcon, label: 'Failed' },
    pending: {
        color: STATUS.warning.fg,
        Icon: HourglassEmptyIcon,
        label: 'Pending',
    },
    opened: { color: ACCENT.text, Icon: CheckCircleIcon, label: 'Opened' },
    clicked: { color: CATEGORY.green, Icon: CheckCircleIcon, label: 'Clicked' },
}

function ShimmerBlock({ height = 80, borderRadius = RADIUS.md }) {
    return (
        <Box
            sx={{
                height,
                borderRadius: `${borderRadius}px`,
                overflow: 'hidden',
                background: `linear-gradient(90deg, ${SURFACE.subtle} 25%, ${SURFACE.hover} 50%, ${SURFACE.subtle} 75%)`,
                backgroundSize: '800px 100%',
                animation: `${shimmer} 1.5s infinite linear`,
            }}
        />
    )
}

function ChannelChip({ channel }) {
    const Icon = CHANNEL_ICONS[channel] || EmailIcon
    const color = CHANNEL_COLOR[channel] || CATEGORY.orange
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: `${RADIUS.sm}px`,
                background: tint(color, 0.08),
                border: `1px solid ${tint(color, 0.2)}`,
            }}
        >
            <Icon sx={{ fontSize: ICON.xs, color }} />
            <Typography
                variant="caption"
                sx={{
                    color,
                    fontWeight: 600,
                    textTransform: 'capitalize',
                }}
            >
                {channel}
            </Typography>
        </Box>
    )
}

function StatusChip({ status }) {
    const cfg = STATUS_CONFIG[status] || {
        color: ACCENT.text,
        Icon: CheckCircleIcon,
        label: status,
    }
    const { Icon, color, label } = cfg
    return (
        <Chip
            icon={
                <Icon
                    sx={{
                        fontSize: `${ICON.xs}px !important`,
                        color: `${color} !important`,
                    }}
                />
            }
            label={label}
            size="small"
            sx={{
                background: tint(color, 0.08),
                color,
                border: `1px solid ${tint(color, 0.2)}`,
                typography: 'caption',
                height: 20,
                fontWeight: 600,
            }}
        />
    )
}

function PipelineCard({ pipeline, index }) {
    const navigate = useNavigate()
    const [expanded, setExpanded] = useState(false)
    return (
        <Paper
            elevation={0}
            sx={{
                ...cardSx,
                p: 0,
                animation: `${fadeIn} ${0.1 + index * 0.04}s ease`,
                overflow: 'hidden',
            }}
        >
            <Box
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setExpanded((v) => !v)
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { background: SURFACE.cardHover },
                }}
                onClick={() => setExpanded((v) => !v)}
            >
                <Box sx={{ flex: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 0.75,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography
                            variant="h6"
                            component="p"
                            sx={{ color: TEXT.primary }}
                        >
                            {pipeline.name}
                        </Typography>
                        {pipeline.channel && (
                            <ChannelChip channel={pipeline.channel} />
                        )}
                        {pipeline.status && (
                            <StatusChip status={pipeline.status} />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {pipeline.sent_at && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <AccessTimeIcon
                                    sx={{
                                        fontSize: ICON.xs,
                                        color: TEXT.faint,
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{ color: TEXT.faint }}
                                >
                                    {pipeline.sent_at}
                                </Typography>
                            </Box>
                        )}
                        {pipeline.total_recipients != null && (
                            <Typography
                                variant="caption"
                                sx={{ color: TEXT.faint }}
                            >
                                {pipeline.total_recipients.toLocaleString()}{' '}
                                recipients
                            </Typography>
                        )}
                    </Box>
                    {pipeline.relevance_reason && (
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{
                                color: ACCENT.text,
                                mt: 0.75,
                                fontStyle: 'italic',
                            }}
                        >
                            {pipeline.relevance_reason}
                        </Typography>
                    )}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        ml: 'auto',
                        flexShrink: 0,
                    }}
                >
                    <Tooltip title="Open pipeline">
                        <IconButton
                            size="small"
                            aria-label="Open pipeline"
                            onClick={(e) => {
                                e.stopPropagation()
                                // Its edit page (it went to "/", the home page,
                                // where the pipeline list once lived).
                                navigate(
                                    `/pipelines/edit/${encodeURIComponent(
                                        pipeline.id
                                    )}`
                                )
                            }}
                            sx={{
                                color: TEXT.faint,
                                '&:hover': {
                                    color: ACCENT.strong,
                                    background: ACCENT.bg12,
                                },
                            }}
                        >
                            <OpenInNewIcon sx={{ fontSize: ICON.sm }} />
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        size="small"
                        aria-label={
                            expanded
                                ? 'Collapse pipeline details'
                                : 'Expand pipeline details'
                        }
                        aria-expanded={expanded}
                        tabIndex={-1}
                        sx={{ color: TEXT.faint }}
                    >
                        {expanded ? (
                            <ExpandLessIcon sx={{ fontSize: ICON.md }} />
                        ) : (
                            <ExpandMoreIcon sx={{ fontSize: ICON.md }} />
                        )}
                    </IconButton>
                </Box>
            </Box>
            <Collapse in={expanded}>
                <Box
                    sx={{
                        px: 2,
                        pb: 2,
                        borderTop: `1px solid ${SURFACE.border}`,
                        pt: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 1.5,
                        }}
                    >
                        {[
                            {
                                label: 'Delivered',
                                val: pipeline.delivered_count,
                            },
                            { label: 'Opened', val: pipeline.open_count },
                            { label: 'Clicked', val: pipeline.click_count },
                            { label: 'Replied', val: pipeline.reply_count },
                            { label: 'Failed', val: pipeline.failed_count },
                            {
                                label: 'Unsub',
                                val: pipeline.unsubscribed_count,
                            },
                        ]
                            .filter(
                                (x) => x.val !== undefined && x.val !== null
                            )
                            .map(({ label, val }) => (
                                <Box
                                    key={label}
                                    sx={{
                                        textAlign: 'center',
                                        py: 1,
                                        px: 1.5,
                                        background: SURFACE.subtle,
                                        borderRadius: `${RADIUS.sm}px`,
                                    }}
                                >
                                    <Typography
                                        variant="h3"
                                        component="p"
                                        sx={{
                                            color: TEXT.primary,
                                            lineHeight: 1,
                                        }}
                                    >
                                        {val.toLocaleString()}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        component="p"
                                        sx={{ color: TEXT.faint, mt: 0.25 }}
                                    >
                                        {label}
                                    </Typography>
                                </Box>
                            ))}
                    </Box>
                </Box>
            </Collapse>
        </Paper>
    )
}

function TemplateCard({ template, index }) {
    const navigate = useNavigate()
    return (
        <Paper
            elevation={0}
            sx={{
                ...cardSx,
                p: 2,
                animation: `${fadeIn} ${0.1 + index * 0.04}s ease`,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <Box sx={{ flex: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 0.75,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography
                            variant="h6"
                            component="p"
                            sx={{ color: TEXT.primary }}
                        >
                            {template.name}
                        </Typography>
                        {template.channel && (
                            <ChannelChip channel={template.channel} />
                        )}
                    </Box>
                    {template.body_preview && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: TEXT.muted,
                                lineHeight: 1.6,
                                fontFamily: FONT.mono,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {template.body_preview}
                        </Typography>
                    )}
                    {template.relevance_reason && (
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{
                                color: ACCENT.text,
                                mt: 0.75,
                                fontStyle: 'italic',
                            }}
                        >
                            {template.relevance_reason}
                        </Typography>
                    )}
                </Box>
                <Tooltip title="Open template">
                    <IconButton
                        size="small"
                        aria-label="Open template"
                        onClick={() =>
                            navigate('/templates', {
                                state: { templateId: template.id },
                            })
                        }
                        sx={{
                            color: TEXT.faint,
                            '&:hover': {
                                color: ACCENT.strong,
                                background: ACCENT.bg12,
                            },
                        }}
                    >
                        <OpenInNewIcon sx={{ fontSize: ICON.sm }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    )
}

function CommHistoryRow({ record, index }) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto auto',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${SURFACE.border}`,
                animation: `${fadeIn} ${0.05 + index * 0.03}s ease`,
                '&:last-child': { borderBottom: 'none' },
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant="body2"
                    sx={{
                        color: TEXT.primary,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {record.event_name || record.event_id}
                </Typography>
                {record.template_name && (
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{
                            color: TEXT.muted,
                            mt: 0.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {record.template_name}
                    </Typography>
                )}
            </Box>
            {record.channel && <ChannelChip channel={record.channel} />}
            {record.status && <StatusChip status={record.status} />}
            <Typography
                variant="caption"
                sx={{
                    color: TEXT.faint,
                    textAlign: 'right',
                    minWidth: 100,
                }}
            >
                {record.sent_at}
            </Typography>
        </Box>
    )
}

export default function CommsFinder() {
    const location = useLocation()
    const [query, setQuery] = useState('')
    const [identity, setIdentity] = useState('')
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState('')
    const [activeTab, setActiveTab] = useState('all')
    const run = useToolRun('comms-finder')

    useEffect(() => {
        // Reset outputs so re-runs (navigation with new state) behave predictably
        setResult(null)
        setError('')
        const saved = run.recall()
        if (saved) {
            setQuery(saved.query ?? '')
            setIdentity(saved.identity ?? '')
            setResult(saved.result)
            return
        }
        const state = location.state
        if (!state) return
        if (state.query) setQuery(state.query)
        if (state.identity) setIdentity(state.identity)
    }, [location.state])

    const handleSearch = async (q, id) => {
        const searchQuery = q !== undefined ? q : query
        const searchIdentity = id !== undefined ? id : identity
        if (!searchQuery.trim() && !searchIdentity.trim()) return
        setLoading(true)
        setError('')
        setResult(null)
        run.forget()
        try {
            const trimmedIdentity = searchIdentity.trim()
            const res = await AIService.commsFinder({
                query: searchQuery || undefined,
                identity: trimmedIdentity || undefined,
                // The API defaults to email; a phone number looked up on the
                // email index always came back empty.
                ...(trimmedIdentity && {
                    identity_type: trimmedIdentity.includes('@')
                        ? 'email'
                        : 'mobile_no',
                }),
            })
            const found = res.data?.result
            setResult(found)
            if (found)
                run.remember({
                    query: searchQuery,
                    identity: searchIdentity,
                    result: found,
                })
        } catch (e) {
            setError(
                e?.response?.data?.message || 'Search failed. Please try again.'
            )
        } finally {
            setLoading(false)
        }
    }

    const pipelines = result?.pipelines || []
    const templates = result?.templates || []
    const commHistory = result?.comm_history || []

    const tabs = [
        { key: 'all', label: 'All' },
        { key: 'pipelines', label: `Pipelines (${pipelines.length})` },
        { key: 'templates', label: `Templates (${templates.length})` },
        { key: 'history', label: `Comm History (${commHistory.length})` },
    ]

    // A faint dot grid in the text colour, so it shows on either theme.
    const dotGrid = {
        backgroundImage: `radial-gradient(${tint(
            TEXT.primary,
            0.06
        )} 1px, transparent 1px)`,
        backgroundSize: '28px 28px',
    }

    return (
        <Box sx={{ ...pageSx, ...dotGrid }}>
            <Box sx={{ maxWidth: 980, mx: 'auto', p: { xs: 3, md: 5 } }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 1,
                        }}
                    >
                        <Box
                            sx={{
                                background: GRADIENT.brand,
                                borderRadius: `${RADIUS.md}px`,
                                p: 1,
                                display: 'flex',
                            }}
                        >
                            {/* Deep Blue on the orange-to-yellow badge in
                                both themes (Almost White on its yellow end
                                is 1.8:1). */}
                            <SearchIcon
                                sx={{ color: ON_ORANGE, fontSize: ICON.lg }}
                            />
                        </Box>
                        <Typography
                            variant="h3"
                            component="p"
                            sx={{ color: TEXT.primary }}
                        >
                            Comms Finder
                        </Typography>
                    </Box>
                    <Typography
                        variant="body2"
                        sx={{ color: TEXT.faint, ml: 0.5 }}
                    >
                        Search pipelines, templates, and recipient history using
                        natural language.
                    </Typography>
                </Box>

                {/* Search form */}
                <Paper elevation={0} sx={{ ...cardSx, p: 3, mb: 3 }}>
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <TextField
                            fullWidth
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) =>
                                e.key === 'Enter' && handleSearch()
                            }
                            label="What are you looking for?"
                            placeholder="e.g. all batch reminders sent last week, templates with click rate > 5%, failed campaigns in June"
                            multiline
                            rows={2}
                        />

                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'flex-end',
                            }}
                        >
                            <Box sx={{ flex: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 0.75,
                                    }}
                                >
                                    <PersonSearchIcon
                                        sx={{
                                            fontSize: ICON.sm,
                                            color: TEXT.faint,
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{ color: TEXT.faint }}
                                    >
                                        Recipient trace (optional)
                                    </Typography>
                                </Box>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={identity}
                                    onChange={(e) =>
                                        setIdentity(e.target.value)
                                    }
                                    placeholder="email or mobile — e.g. priya.nair@bhanzu.com"
                                    inputProps={{
                                        'aria-label': 'Recipient trace',
                                    }}
                                />
                            </Box>
                            <Button
                                variant="contained"
                                onClick={() => handleSearch()}
                                disabled={
                                    loading ||
                                    (!query.trim() && !identity.trim())
                                }
                                startIcon={
                                    loading ? (
                                        <CircularProgress
                                            size={16}
                                            sx={{ color: ON_ORANGE }}
                                        />
                                    ) : (
                                        <SearchIcon />
                                    )
                                }
                                sx={{
                                    // Match the adjacent size="small" input's
                                    // box (not the label above it) so the two
                                    // share a height and a bottom edge.
                                    height: 33,
                                    minHeight: 0,
                                    // The theme's primary button (orange,
                                    // Deep Blue label, its own hover and
                                    // disabled states): a label on the brand
                                    // gradient failed AA at its yellow end.
                                    fontWeight: 600,
                                    px: 3,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {loading ? 'Searching…' : 'Search'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                {loading && (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        {[60, 90, 90, 70, 90].map((h, i) => (
                            <ShimmerBlock key={i} height={h} />
                        ))}
                    </Box>
                )}

                {result && (
                    <Box sx={{ animation: `${fadeIn} 0.3s ease` }}>
                        {/* Interpretation strip */}
                        {result.interpretation && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                    mb: 2.5,
                                    px: 2,
                                    py: 1.25,
                                    borderRadius: `${RADIUS.md}px`,
                                    background: ACCENT.bg08,
                                    border: `1px solid ${tint(
                                        BRAND.orange,
                                        0.2
                                    )}`,
                                }}
                            >
                                <SearchIcon
                                    sx={{
                                        fontSize: ICON.sm,
                                        color: ACCENT.strong,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: TEXT.secondary,
                                        fontStyle: 'italic',
                                    }}
                                >
                                    {result.interpretation}
                                </Typography>
                            </Box>
                        )}

                        {/* Tab chips */}
                        {(pipelines.length > 0 ||
                            templates.length > 0 ||
                            commHistory.length > 0) && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    mb: 2.5,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {tabs.map((t) => (
                                    <Chip
                                        key={t.key}
                                        label={t.label}
                                        size="small"
                                        onClick={() => setActiveTab(t.key)}
                                        sx={{
                                            cursor: 'pointer',
                                            background:
                                                activeTab === t.key
                                                    ? ACCENT.bg15
                                                    : SURFACE.subtle,
                                            color:
                                                activeTab === t.key
                                                    ? ACCENT.text
                                                    : TEXT.muted,
                                            border: `1px solid ${
                                                activeTab === t.key
                                                    ? ACCENT.border55
                                                    : SURFACE.border
                                            }`,
                                            fontWeight:
                                                activeTab === t.key ? 700 : 400,
                                            typography: 'caption',
                                        }}
                                    />
                                ))}
                            </Box>
                        )}

                        {/* Pipelines section */}
                        {(activeTab === 'all' || activeTab === 'pipelines') &&
                            pipelines.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1.5,
                                        }}
                                    >
                                        <PolylineIcon
                                            sx={{
                                                fontSize: ICON.sm,
                                                color: CATEGORY.orange,
                                            }}
                                        />
                                        <Typography
                                            variant="overline"
                                            sx={{ color: TEXT.muted }}
                                        >
                                            Pipelines
                                        </Typography>
                                        <Chip
                                            label={pipelines.length}
                                            size="small"
                                            sx={{
                                                background: tint(
                                                    CATEGORY.orange,
                                                    0.15
                                                ),
                                                color: CATEGORY.orange,
                                                typography: 'caption',
                                                height: 18,
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'grid', gap: 1.5 }}>
                                        {pipelines.map((p, i) => (
                                            <PipelineCard
                                                key={p.id || i}
                                                pipeline={p}
                                                index={i}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                        {/* Templates section */}
                        {(activeTab === 'all' || activeTab === 'templates') &&
                            templates.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1.5,
                                        }}
                                    >
                                        <ArticleIcon
                                            sx={{
                                                fontSize: ICON.sm,
                                                color: CATEGORY.cyan,
                                            }}
                                        />
                                        <Typography
                                            variant="overline"
                                            sx={{ color: TEXT.muted }}
                                        >
                                            Templates
                                        </Typography>
                                        <Chip
                                            label={templates.length}
                                            size="small"
                                            sx={{
                                                background: tint(
                                                    CATEGORY.cyan,
                                                    0.15
                                                ),
                                                color: CATEGORY.cyan,
                                                typography: 'caption',
                                                height: 18,
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'grid', gap: 1.5 }}>
                                        {templates.map((t, i) => (
                                            <TemplateCard
                                                key={t.id || i}
                                                template={t}
                                                index={i}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                        {/* Comm history */}
                        {(activeTab === 'all' || activeTab === 'history') &&
                            commHistory.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1.5,
                                        }}
                                    >
                                        <AccessTimeIcon
                                            sx={{
                                                fontSize: ICON.sm,
                                                color: CATEGORY.amber,
                                            }}
                                        />
                                        <Typography
                                            variant="overline"
                                            sx={{ color: TEXT.muted }}
                                        >
                                            Comm History
                                            {identity && (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        color: ACCENT.text,
                                                        ml: 0.75,
                                                    }}
                                                >
                                                    for {identity}
                                                </Box>
                                            )}
                                        </Typography>
                                        <Chip
                                            label={commHistory.length}
                                            size="small"
                                            sx={{
                                                background: tint(
                                                    CATEGORY.amber,
                                                    0.15
                                                ),
                                                color: CATEGORY.amber,
                                                typography: 'caption',
                                                height: 18,
                                            }}
                                        />
                                    </Box>
                                    <Paper
                                        elevation={0}
                                        sx={{ ...cardSx, overflow: 'hidden' }}
                                    >
                                        {commHistory.map((r, i) => (
                                            <CommHistoryRow
                                                key={r.id || i}
                                                record={r}
                                                index={i}
                                            />
                                        ))}
                                    </Paper>
                                </Box>
                            )}

                        {/* Empty state */}
                        {pipelines.length === 0 &&
                            templates.length === 0 &&
                            commHistory.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <SearchIcon
                                        sx={{
                                            fontSize: ICON.hero,
                                            color: TEXT.decorative,
                                            mb: 1,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{ color: TEXT.faint }}
                                    >
                                        No results found. Try rephrasing your
                                        query.
                                    </Typography>
                                </Box>
                            )}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
